/**
 * Gulf Countries + Jordan Detection Utility
 * Detects if a user is from Gulf Cooperation Council (GCC) countries + Jordan
 */
import React from "react";

// Gulf Cooperation Council (GCC) countries + Jordan
export const GULF_COUNTRIES = {
  // Country codes (ISO 3166-1 alpha-2)
  codes: ["AE", "SA", "KW", "QA", "BH", "OM", "JO"],
  // Country names (multiple variations for better detection)
  names: [
    // United Arab Emirates
    "united arab emirates",
    "uae",
    "emirates",
    "dubai",
    "abu dhabi",
    // Saudi Arabia
    "saudi arabia",
    "saudi",
    "ksa",
    "kingdom of saudi arabia",
    "riyadh",
    "jeddah",
    // Kuwait
    "kuwait",
    "kuwait city",
    // Qatar
    "qatar",
    "doha",
    // Bahrain
    "bahrain",
    "manama",
    // Oman
    "oman",
    "muscat",
    "sultanate of oman",
    // Jordan
    "jordan",
    "hashemite kingdom of jordan",
    "amman",
    "irbid",
    "zarqa",
  ],
  // Major cities for more precise detection
  cities: [
    // UAE cities
    "dubai",
    "abu dhabi",
    "sharjah",
    "ajman",
    "fujairah",
    "ras al khaimah",
    "umm al quwain",
    // Saudi cities
    "riyadh",
    "jeddah",
    "mecca",
    "medina",
    "dammam",
    "khobar",
    "dhahran",
    "tabuk",
    "abha",
    // Kuwait cities
    "kuwait city",
    "hawalli",
    "salmiya",
    // Qatar cities
    "doha",
    "al rayyan",
    "al wakrah",
    // Bahrain cities
    "manama",
    "muharraq",
    "riffa",
    // Oman cities
    "muscat",
    "salalah",
    "nizwa",
    "sur",
    // Jordan cities
    "amman",
    "irbid",
    "zarqa",
    "aqaba",
    "salt",
    "madaba",
    "karak",
    "mafraq",
  ],
} as const;

export type GulfCountryCode = (typeof GULF_COUNTRIES.codes)[number];

export type GulfDetectionResult = {
  isGulfCountry: boolean;
  countryCode?: string; // Changed to support all country codes
  countryName?: string;
  detectedCity?: string;
  region?: string;
  timezone?: string;
  currency?: string;
  languages?: string[];
  latitude?: number;
  longitude?: number;
  confidence: "high" | "medium" | "low";
  detectionMethod:
    | "country_code"
    | "country_name"
    | "city_name"
    | "ip_geolocation"
    | "browser_locale"
    | "timezone";
  city?: string; // Added for compatibility
  // Enhanced fraud prevention fields from ipapi.co
  ip?: string;              // Customer IP address
  org?: string;             // ISP/Organization name (for VPN detection)
  asn?: string;             // AS number
  postal?: string;          // Postal code for AVS matching
  inEu?: boolean;           // Whether IP is in EU (for GDPR)
  countryCallingCode?: string; // Phone country code
  currencyName?: string;    // Full currency name
  utcOffset?: string;       // UTC offset string
  countryArea?: number;     // Country area in sq km
  countryPopulation?: number; // Country population
  continentCode?: string;   // Continent code (EU, AS, etc.)
  network?: string;         // IP network/CIDR
};

/**
 * Detects if a country code is from Gulf countries
 */
export function isGulfCountryCode(countryCode: string): boolean {
  return GULF_COUNTRIES.codes.includes(countryCode.toUpperCase() as GulfCountryCode);
}

/**
 * Detects Gulf country from country name or city
 */
export function detectGulfFromLocation(location: string): GulfDetectionResult {
  if (!location) {
    return {
      isGulfCountry: false,
      confidence: "low",
      detectionMethod: "country_name",
    };
  }

  const normalizedLocation = location.toLowerCase().trim();

  // Check country codes first (highest confidence)
  for (const code of GULF_COUNTRIES.codes) {
    if (normalizedLocation === code.toLowerCase()) {
      return {
        isGulfCountry: true,
        countryCode: code,
        confidence: "high",
        detectionMethod: "country_code",
      };
    }
  }

  // Check country names (high confidence)
  for (const countryName of GULF_COUNTRIES.names) {
    if (normalizedLocation.includes(countryName)) {
      const countryCode = getCountryCodeFromName(countryName);
      return {
        isGulfCountry: true,
        countryCode,
        countryName,
        confidence: "high",
        detectionMethod: "country_name",
      };
    }
  }

  // Check cities (medium confidence)
  for (const city of GULF_COUNTRIES.cities) {
    if (normalizedLocation.includes(city)) {
      const countryCode = getCountryCodeFromCity(city);
      return {
        isGulfCountry: true,
        countryCode,
        detectedCity: city,
        confidence: "medium",
        detectionMethod: "city_name",
      };
    }
  }

  return {
    isGulfCountry: false,
    confidence: "low",
    detectionMethod: "country_name",
  };
}

/**
 * Detects Gulf country using browser timezone
 */
export function detectGulfFromTimezone(): GulfDetectionResult {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const gulfTimezones: Record<string, GulfCountryCode> = {
      "Asia/Dubai": "AE",
      "Asia/Riyadh": "SA",
      "Asia/Kuwait": "KW",
      "Asia/Qatar": "QA",
      "Asia/Bahrain": "BH",
      "Asia/Muscat": "OM",
      "Asia/Amman": "JO",
    };

    const countryCode = gulfTimezones[timezone];
    if (countryCode) {
      return {
        isGulfCountry: true,
        countryCode,
        confidence: "medium",
        detectionMethod: "timezone",
      };
    }
  } catch (error) {
    console.warn("Failed to detect timezone:", error);
  }

  return {
    isGulfCountry: false,
    confidence: "low",
    detectionMethod: "timezone",
  };
}

/**
 * Detects Gulf country using browser locale
 */
export function detectGulfFromLocale(): GulfDetectionResult {
  try {
    const locale = navigator.language || navigator.languages?.[0];
    if (!locale) throw new Error("No locale available");

    // Extract country code from locale (e.g., 'ar-SA' -> 'SA')
    const countryCode = locale.split("-")[1]?.toUpperCase();

    if (countryCode && isGulfCountryCode(countryCode)) {
      return {
        isGulfCountry: true,
        countryCode: countryCode as GulfCountryCode,
        confidence: "medium",
        detectionMethod: "browser_locale",
      };
    }
  } catch (error) {
    console.warn("Failed to detect locale:", error);
  }

  return {
    isGulfCountry: false,
    confidence: "low",
    detectionMethod: "browser_locale",
  };
}

/**
 * Comprehensive Gulf country detection using multiple methods
 */
export async function detectGulfCountry(options?: {
  location?: string;
  countryCode?: string;
  useGeolocation?: boolean;
  useTimezone?: boolean;
  useLocale?: boolean;
}): Promise<GulfDetectionResult> {
  const {
    location,
    countryCode,
    useGeolocation = true,
    useTimezone = true,
    useLocale = true,
  } = options || {};

  // Method 1: Direct country code check (highest priority)
  if (countryCode && isGulfCountryCode(countryCode)) {
    return {
      isGulfCountry: true,
      countryCode: countryCode.toUpperCase() as GulfCountryCode,
      confidence: "high",
      detectionMethod: "country_code",
    };
  }

  // Method 2: Location string analysis
  if (location) {
    const locationResult = detectGulfFromLocation(location);
    if (locationResult.isGulfCountry) {
      return locationResult;
    }
  }

  // Method 3: Browser timezone
  if (useTimezone) {
    const timezoneResult = detectGulfFromTimezone();
    if (timezoneResult.isGulfCountry) {
      return timezoneResult;
    }
  }

  // Method 4: Browser locale
  if (useLocale) {
    const localeResult = detectGulfFromLocale();
    if (localeResult.isGulfCountry) {
      return localeResult;
    }
  }

  // Method 5: IP Geolocation (if enabled and available)
  if (useGeolocation) {
    try {
      const geoResult = await detectGulfFromIP();
      if (geoResult.isGulfCountry) {
        return geoResult;
      }
    } catch (error) {
      console.warn("IP geolocation failed:", error);
    }
  }

  // No Gulf country detected
  return {
    isGulfCountry: false,
    confidence: "low",
    detectionMethod: "country_code",
  };
}

// Cache configuration
const LOCATION_CACHE_KEY = "ip_location_cache";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const IPAPI_KEY = "TIqP1t56aAnpS9BS0mkv0RLB0gSs3mhB9dUDL5Yo2NzaClNwbS";

/**
 * Cache location data in localStorage
 */
function cacheLocationData(data: any): void {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
      version: "2.0", // Version 2.0: Added fraud prevention fields (ip, org, asn, postal, etc.)
    };
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn("Failed to cache location data:", error);
  }
}

/**
 * Get cached location data if valid
 */
function getCachedLocationData(): any | null {
  try {
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp, version } = JSON.parse(cached);

    // Check if cache is expired
    if (Date.now() - timestamp > CACHE_DURATION) {
      localStorage.removeItem(LOCATION_CACHE_KEY);
      return null;
    }

    // Check version compatibility (v2.0 includes fraud prevention fields)
    if (version !== "2.0") {
      localStorage.removeItem(LOCATION_CACHE_KEY);
      return null;
    }

    return data;
  } catch (error) {
    console.warn("Failed to get cached location:", error);
    localStorage.removeItem(LOCATION_CACHE_KEY);
    return null;
  }
}

/**
 * Process raw location data from ipapi.co
 * Enhanced to capture fraud prevention signals
 */
function processIpApiData(data: any): GulfDetectionResult {
  const countryCode = data.country_code;
  const isGulf = countryCode && isGulfCountryCode(countryCode);

  return {
    isGulfCountry: isGulf,
    countryCode: countryCode, // Return country code for all countries
    countryName: data.country_name,
    detectedCity: data.city,
    city: data.city, // Added for compatibility
    region: data.region,
    timezone: data.timezone,
    currency: data.currency,
    languages: data.languages
      ? data.languages.split(",").map((lang: string) => lang.trim())
      : undefined,
    latitude: data.latitude,
    longitude: data.longitude,
    confidence: "high",
    detectionMethod: "ip_geolocation",
    // Enhanced fraud prevention fields
    ip: data.ip,                          // Customer IP address
    org: data.org,                        // ISP/Organization (for VPN detection)
    asn: data.asn,                        // AS number
    postal: data.postal,                  // Postal code for AVS
    inEu: data.in_eu,                     // EU membership flag
    countryCallingCode: data.country_calling_code, // Phone country code
    currencyName: data.currency_name,     // Full currency name
    utcOffset: data.utc_offset,           // UTC offset string
    countryArea: data.country_area,       // Country area
    countryPopulation: data.country_population, // Country population
    continentCode: data.continent_code,   // Continent code
    network: data.network,                // IP network/CIDR
  };
}

/**
 * Detects Gulf country using IP geolocation with caching and API key
 */
async function detectGulfFromIP(): Promise<GulfDetectionResult> {
  try {
    // Check cache first
    const cachedData = getCachedLocationData();
    if (cachedData) {
      return processIpApiData(cachedData);
    }

    // Make API request with API key
    const apiUrl = `https://ipapi.co/json/?key=${IPAPI_KEY}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "User-Agent": "Stylion/1.0",
      },
    });

    // Handle rate limiting
    if (response.status === 429) {
      console.warn("IP API rate limit exceeded");
      return {
        isGulfCountry: false,
        confidence: "low",
        detectionMethod: "ip_geolocation",
      };
    }

    if (!response.ok) {
      throw new Error(`Geolocation API failed: ${response.status}`);
    }

    const data = await response.json();

    // Check for API errors
    if (data.error) {
      console.warn("IP API error:", data.reason || data.error);
      return {
        isGulfCountry: false,
        confidence: "low",
        detectionMethod: "ip_geolocation",
      };
    }

    // Cache the successful response
    cacheLocationData(data);

    // Process and return the data
    return processIpApiData(data);
  } catch (error) {
    console.warn("IP geolocation detection failed:", error);
    return {
      isGulfCountry: false,
      confidence: "low",
      detectionMethod: "ip_geolocation",
    };
  }
}

/**
 * Helper function to get country code from country name
 */
function getCountryCodeFromName(countryName: string): GulfCountryCode | undefined {
  const nameToCode: Record<string, GulfCountryCode> = {
    "united arab emirates": "AE",
    uae: "AE",
    emirates: "AE",
    "saudi arabia": "SA",
    saudi: "SA",
    ksa: "SA",
    "kingdom of saudi arabia": "SA",
    kuwait: "KW",
    qatar: "QA",
    bahrain: "BH",
    oman: "OM",
    "sultanate of oman": "OM",
  };

  return nameToCode[countryName.toLowerCase()];
}

/**
 * Helper function to get country code from city name
 */
function getCountryCodeFromCity(cityName: string): GulfCountryCode | undefined {
  const cityToCode: Record<string, GulfCountryCode> = {
    // UAE cities
    dubai: "AE",
    "abu dhabi": "AE",
    sharjah: "AE",
    ajman: "AE",
    fujairah: "AE",
    "ras al khaimah": "AE",
    "umm al quwain": "AE",
    // Saudi cities
    riyadh: "SA",
    jeddah: "SA",
    mecca: "SA",
    medina: "SA",
    dammam: "SA",
    khobar: "SA",
    dhahran: "SA",
    tabuk: "SA",
    abha: "SA",
    // Kuwait cities
    "kuwait city": "KW",
    hawalli: "KW",
    salmiya: "KW",
    // Qatar cities
    doha: "QA",
    "al rayyan": "QA",
    "al wakrah": "QA",
    // Bahrain cities
    manama: "BH",
    muharraq: "BH",
    riffa: "BH",
    // Oman cities
    muscat: "OM",
    salalah: "OM",
    nizwa: "OM",
    sur: "OM",
    // Jordan cities
    amman: "JO",
    irbid: "JO",
    zarqa: "JO",
    aqaba: "JO",
    salt: "JO",
    madaba: "JO",
    karak: "JO",
    mafraq: "JO",
  };

  return cityToCode[cityName.toLowerCase()];
}

/**
 * Get Gulf country information
 */
export function getGulfCountryInfo(countryCode: GulfCountryCode) {
  const countryInfo: Record<
    GulfCountryCode,
    {
      name: string;
      arabicName: string;
      currency: string;
      currencySymbol: string;
      timezone: string;
      locale: string;
      rtl: boolean;
    }
  > = {
    AE: {
      name: "United Arab Emirates",
      arabicName: "الإمارات العربية المتحدة",
      currency: "AED",
      currencySymbol: "د.إ",
      timezone: "Asia/Dubai",
      locale: "ar-AE",
      rtl: true,
    },
    SA: {
      name: "Saudi Arabia",
      arabicName: "المملكة العربية السعودية",
      currency: "SAR",
      currencySymbol: "ر.س",
      timezone: "Asia/Riyadh",
      locale: "ar-SA",
      rtl: true,
    },
    KW: {
      name: "Kuwait",
      arabicName: "الكويت",
      currency: "KWD",
      currencySymbol: "د.ك",
      timezone: "Asia/Kuwait",
      locale: "ar-KW",
      rtl: true,
    },
    QA: {
      name: "Qatar",
      arabicName: "قطر",
      currency: "QAR",
      currencySymbol: "ر.ق",
      timezone: "Asia/Qatar",
      locale: "ar-QA",
      rtl: true,
    },
    BH: {
      name: "Bahrain",
      arabicName: "البحرين",
      currency: "BHD",
      currencySymbol: "د.ب",
      timezone: "Asia/Bahrain",
      locale: "ar-BH",
      rtl: true,
    },
    OM: {
      name: "Oman",
      arabicName: "عُمان",
      currency: "OMR",
      currencySymbol: "ر.ع",
      timezone: "Asia/Muscat",
      locale: "ar-OM",
      rtl: true,
    },
    JO: {
      name: "Jordan",
      arabicName: "الأردن",
      currency: "USD", // JOD not supported by Stripe US accounts
      currencySymbol: "$",
      timezone: "Asia/Amman",
      locale: "ar-JO",
      rtl: true,
    },
  };

  return countryInfo[countryCode];
}

/**
 * Allow manual location override for testing
 */
export function setManualLocation(countryCode: string): void {
  localStorage.setItem("manual_location_override", countryCode);
  // Clear cache to force re-detection
  localStorage.removeItem(LOCATION_CACHE_KEY);
}

/**
 * Get manual location override if set
 */
export function getManualLocation(): string | null {
  return localStorage.getItem("manual_location_override");
}

/**
 * Clear manual location override
 */
export function clearManualLocation(): void {
  localStorage.removeItem("manual_location_override");
  localStorage.removeItem(LOCATION_CACHE_KEY);
}

/**
 * Smart location detection with multiple strategies
 */
export async function smartLocationDetection(): Promise<GulfDetectionResult> {
  // 1. Check URL params for testing (e.g., ?country=EG or ?country=US)
  const urlParams = new URLSearchParams(window.location.search);
  const urlCountry = urlParams.get("country");
  if (urlCountry) {
    const countryCode = urlCountry.toUpperCase();
    console.log("Using URL parameter for location:", countryCode);
    return {
      isGulfCountry: isGulfCountryCode(countryCode),
      countryCode: countryCode,
      confidence: "high",
      detectionMethod: "country_code",
    };
  }

  // 2. Check manual override
  const manualLocation = getManualLocation();
  if (manualLocation) {
    const countryCode = manualLocation.toUpperCase();
    console.log("Using manual location override:", countryCode);
    return {
      isGulfCountry: isGulfCountryCode(countryCode),
      countryCode: countryCode,
      confidence: "high",
      detectionMethod: "country_code",
    };
  }

  // 3. Try IP geolocation (with caching)
  try {
    const ipResult = await detectGulfFromIP();
    // Return IP result if we have a country code (any country, not just Gulf)
    if (ipResult.countryCode) {
      return ipResult;
    }
  } catch {
    // IP detection failed, trying other methods
  }

  // 4. Try timezone detection
  const timezoneResult = detectGulfFromTimezone();
  if (timezoneResult.isGulfCountry) {
    return timezoneResult;
  }

  // 5. Try browser locale
  const localeResult = detectGulfFromLocale();
  if (localeResult.isGulfCountry) {
    return localeResult;
  }

  // 6. No Gulf country detected
  return {
    isGulfCountry: false,
    confidence: "low",
    detectionMethod: "country_code",
  };
}

/**
 * React hook for Gulf country detection with smart strategies
 */
export function useGulfCountryDetection() {
  const [detection, setDetection] = React.useState<GulfDetectionResult | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const detectCountry = async () => {
      try {
        setLoading(true);
        // Use smart detection instead of the old method
        const result = await smartLocationDetection();
        setDetection(result);
      } catch (error) {
        console.error("Gulf country detection failed:", error);
        setDetection({
          isGulfCountry: false,
          confidence: "low",
          detectionMethod: "country_code",
        });
      } finally {
        setLoading(false);
      }
    };

    detectCountry();
  }, []);

  return { detection, loading };
}

/**
 * Get Gulf-specific templates (ATS-friendly and Arabic-compatible)
 */
export function getGulfSpecificTemplates() {
  return [
    "classic", // Professional, clean
    "confidence", // Strong, professional
    "executive", // Premium executive
    "modern", // Contemporary
    "simple", // Minimal, ATS-friendly
    "straight", // Strong, traditional
    "onyx", // Professional dark theme
    "leafish", // Fresh, professional
    "elegance", // Sophisticated, minimal
  ];
}

/**
 * Get Gulf-specific job market insights
 */
export function getGulfJobMarketData(countryCode?: GulfCountryCode) {
  const gulfMarketData = {
    AE: {
      topIndustries: ["Technology", "Finance", "Tourism", "Real Estate", "Healthcare"],
      averageSalary: "AED 8,000 - 25,000",
      jobGrowth: "+12%",
      marketTrends: ["Digital transformation", "Sustainability", "AI adoption", "Remote work"],
    },
    SA: {
      topIndustries: ["Oil & Gas", "Technology", "Healthcare", "Construction", "Finance"],
      averageSalary: "SAR 7,000 - 22,000",
      jobGrowth: "+15%",
      marketTrends: ["Vision 2030", "NEOM project", "Tech innovation", "Women workforce"],
    },
    KW: {
      topIndustries: ["Oil & Gas", "Finance", "Healthcare", "Education", "Technology"],
      averageSalary: "KWD 600 - 2,000",
      jobGrowth: "+8%",
      marketTrends: ["Economic diversification", "Digital banking", "Healthcare expansion"],
    },
    QA: {
      topIndustries: ["Oil & Gas", "Construction", "Finance", "Technology", "Sports"],
      averageSalary: "QAR 8,000 - 30,000",
      jobGrowth: "+10%",
      marketTrends: ["World Cup legacy", "Smart city initiatives", "Fintech growth"],
    },
    BH: {
      topIndustries: ["Finance", "Technology", "Tourism", "Manufacturing", "Healthcare"],
      averageSalary: "BHD 400 - 1,500",
      jobGrowth: "+7%",
      marketTrends: ["Fintech hub", "Startup ecosystem", "Digital transformation"],
    },
    OM: {
      topIndustries: ["Oil & Gas", "Tourism", "Manufacturing", "Agriculture", "Technology"],
      averageSalary: "OMR 400 - 1,200",
      jobGrowth: "+6%",
      marketTrends: ["Economic diversification", "Tourism growth", "SME development"],
    },
    JO: {
      topIndustries: ["Technology", "Healthcare", "Tourism", "Manufacturing", "Finance"],
      averageSalary: "JOD 400 - 1,000",
      jobGrowth: "+8%",
      marketTrends: [
        "Tech hub growth",
        "Digital transformation",
        "Healthcare expansion",
        "Tourism revival",
      ],
    },
  };

  if (countryCode && gulfMarketData[countryCode]) {
    return gulfMarketData[countryCode];
  }

  // Return general Gulf + Jordan region data
  return {
    topIndustries: ["Technology", "Oil & Gas", "Finance", "Healthcare", "Construction"],
    averageSalary: "Competitive regional packages",
    jobGrowth: "+10%",
    marketTrends: [
      "Digital transformation",
      "Economic diversification",
      "Tech hub growth",
      "Sustainability",
    ],
  };
}
